import { AppForm } from '../../../../../../../common/forms/app.form';
import { Injectable, Signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ParcelAppEntityParcelWrite
} from '../../../../../../../openapi';
import { toNumber } from '../../../../../../../services/helpers';
import { DictionaryCommonService } from '../../../../../../../services/dictionary.common.service';
import {
  DictTownRoadItem, DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../../services/dictionary.common.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { optional } from '../../../../../../../common/forms/custom.validators';

@Injectable()
export class LocalisationForm extends AppForm {

  public townWithStreetsList: Signal<DictTownWithStreetsItem[] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getStreetList(townName?: string | null): Array<DictTownStreetItem> {
    return this.townWithStreetsList()?.filter(v => v.name === townName)?.at(0)?.streets || [];
  }

  constructor(private dictService: DictionaryCommonService) {
    super({
      provideRoadNumberAndName: new FormControl(true),
      city: new FormControl(
        '',
        { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }
      ),
      parcels: new FormControl([], {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)]
      }),
      countyRoadArea: new FormControl(null, [Validators.maxLength(255)]),
      bridgeStructureArea: new FormControl(null, [Validators.maxLength(255)]),
      telecommunicationInfrastructureArea: new FormControl(null, [Validators.maxLength(255)])
    }, {
      validators: [
        optional('countyRoadArea', 'bridgeStructureArea', 'telecommunicationInfrastructureArea')
      ]
    });
    this.provideRoadNumberAndNameChange(true);
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');

      this.addControl('street', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  private get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return this.parcels.value;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      provideRoadNumberAndName: this.value.provideRoadNumberAndName,
      city: this.value.city,
      street: this.value?.street || null,
      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      surfaceType: this.value?.surfaceType,
      deviceLocationJustification: this.value?.deviceLocationJustification,
      countyRoadArea: toNumber(this.value?.countyRoadArea),
      bridgeStructureArea: toNumber(this.value?.bridgeStructureArea),
      telecommunicationInfrastructureArea: toNumber(this.value?.telecommunicationInfrastructureArea)
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.provideRoadNumberAndNameChange(!!app?.provideRoadNumberAndName);
    this.parcels.setValue(app?.parcels || []);
    this.patchValue({
      ...app,
      city: app?.city || '',
      provideRoadNumberAndName: !!app?.provideRoadNumberAndName
    });
  }
}
